import { DateTime } from 'luxon';
import { z } from 'zod';

import { ManifestSalutation, getManifestAgeBracket } from './types';

export type ManifestValidationMessages = {
  // Required (per field)
  manifestValidationSalutationRequired?: string;
  manifestValidationFirstNameRequired?: string;
  manifestValidationLastNameRequired?: string;
  manifestValidationStreetAddressRequired?: string;
  manifestValidationZipCodeRequired?: string;
  manifestValidationCountryRequired?: string;
  manifestValidationPassportTypeRequired?: string;
  manifestValidationNationalityRequired?: string;
  manifestValidationPassportNumberRequired?: string;
  manifestValidationPlaceOfBirthRequired?: string;
  manifestValidationPlaceOfIssueRequired?: string;
  manifestValidationDateOfBirthRequired?: string;
  manifestValidationDateOfIssueRequired?: string;
  manifestValidationValidUntilRequired?: string;
  manifestValidationProfessionRequired?: string;
  manifestValidationPreferredLanguageRequired?: string;
  manifestValidationEmergencyNameRequired?: string;
  manifestValidationEmergencyPhoneRequired?: string;
  manifestValidationPhoneRequired?: string;
  manifestValidationMobileRequired?: string;

  // Date semantic (per field)
  manifestValidationDateOfBirthPast?: string;
  manifestValidationDateOfBirthBeforeEmbark?: string;
  manifestValidationDateOfBirthAdultMinAge?: string;
  manifestValidationDateOfBirthChildAge?: string;
  manifestValidationDateOfBirthBabyAge?: string;
  manifestValidationDateOfBirthMaxAge?: string;
  manifestValidationValidUntilFuture?: string;
  manifestValidationDateOfIssueBeforeEmbark?: string;
  manifestValidationDateOfIssueMaxAge?: string;

  // Format (per field)
  manifestValidationZipCodeFormatDE?: string;
  manifestValidationZipCodeFormatATCH?: string;
  manifestValidationPhoneFormat?: string;
  manifestValidationMobileFormat?: string;
  manifestValidationPassportNumberFormat?: string;
  manifestValidationCityFormat?: string;

  // Acceptance (per field)
  manifestValidationApproveMobilePhoneDisclosureMustAccept?: string;
  manifestValidationEntryRequirementsMustAccept?: string;
  manifestValidationHealthTermsMustAccept?: string;
  manifestValidationDataProtectionMustAccept?: string;
  manifestValidationHasETAMustAccept?: string;
  manifestValidationVisaMustAccept?: string;
  manifestValidationPregnant24WeeksMustAccept?: string;

  // Cross-field (already specific)
  manifestValidationEmailInvalid?: string;
  manifestValidationValidUntilAfterIssue?: string;
  // Type-specific grace period the ID must keep beyond the trip's end date.
  // API codes: "R" = Reisepass (+6 months), "P" = Personalausweis (+3 months).
  manifestValidationValidUntilPassportGrace?: string;
  manifestValidationValidUntilIdCardGrace?: string;

  // Generic invalid-date (non-empty input that doesn't parse to a real
  // calendar date — e.g. 31 Feb, month 13, day 0).
  manifestValidationDateInvalid?: string;
};

export type ManifestSchemaOptions = {
  /** First trip's startDate (ISO YYYY-MM-DD) — embark date. */
  embarkDate?: string;
  /** Last trip's endDate (ISO YYYY-MM-DD) — debark date. */
  debarkDate?: string;
  /** Traveller salutation as fetched from the manifest. Only drives the
   * pregnancy-field shape ("Frau" is never user-selectable); the age-bracket
   * check derives from the live `personalInfo.salutation` form value. */
  salutation?: string;
  /** True when the UK-ETA checkbox is rendered — drives the conditional
   * `hasETA` required check. */
  showEtaCheckbox?: boolean;
  /** True when the USA/ESTA checkbox is rendered — drives the conditional
   * `hasVisa` required check. */
  showUsaCheckbox?: boolean;
  /** True when the salutation select is user-editable (manifest not completed
   * and only child/baby options offered) — makes salutation required. */
  salutationRequired?: boolean;
};

// Strict ISO `yyyy-MM-dd` parse via luxon. Returns null for empty input AND
// for invalid calendar dates (31 Feb, month 13, day 0, etc.) — JS's native
// `new Date("2025-02-31")` silently rolls over to March 3, which let
// impossible dates pass through cross-field validation. Luxon's `isValid`
// rejects them.
const parseIsoDate = (v: string): DateTime | null => {
  if (!v) return null;
  const dt = DateTime.fromISO(v, { zone: 'utc' });
  return dt.isValid ? dt : null;
};

const startOfToday = (): DateTime => DateTime.utc().startOf('day');

// Completed years between two dates (birth → at). Returns the age at `at`,
// dropping the partial year if `at` hasn't reached the birth-day yet.
const ageInYearsAt = (birth: DateTime, at: DateTime): number =>
  Math.floor(at.diff(birth, 'years').years);

// Age brackets at embark, derived from the API salutation (see
// `getManifestAgeBracket`):
//   adult (Herr/Frau): ≥ 15y
//   kind  (Kind*):     ≥ 2y and < 15y
//   baby  (Baby*):     < 2y
// Unknown salutations skip the bracket-specific age check.

// Country-specific postal code shapes. Countries not listed accept any
// non-empty value (the per-field `.min(1, required)` still guards emptiness).
// Pattern is matched against trimmed input.
const ZIP_CODE_PATTERNS: Record<string, RegExp> = {
  DE: /^\d{5}$/,
  AT: /^\d{4}$/,
  CH: /^\d{4}$/,
};

/**
 * Single schema covering every within-section rule for the manifest form.
 */
export const createManifestSchema = (
  i18n?: ManifestValidationMessages,
  opts?: ManifestSchemaOptions,
) => {
  const msg = {
    salutationRequired:
      i18n?.manifestValidationSalutationRequired ??
      'Bitte wählen Sie eine Anrede aus.',
    firstNameRequired:
      i18n?.manifestValidationFirstNameRequired ??
      'Bitte geben Sie Ihren Vornamen an.',
    lastNameRequired:
      i18n?.manifestValidationLastNameRequired ??
      'Bitte geben Sie Ihren Nachnamen an.',
    streetAddressRequired:
      i18n?.manifestValidationStreetAddressRequired ??
      'Bitte geben Sie Ihre Straße und Hausnummer an.',
    zipCodeRequired:
      i18n?.manifestValidationZipCodeRequired ??
      'Bitte geben Sie Ihre Postleitzahl an.',
    countryRequired:
      i18n?.manifestValidationCountryRequired ??
      'Bitte wählen Sie Ihr Land aus.',
    passportTypeRequired:
      i18n?.manifestValidationPassportTypeRequired ??
      'Bitte wählen Sie Ihren Ausweistyp aus.',
    nationalityRequired:
      i18n?.manifestValidationNationalityRequired ??
      'Bitte wählen Sie Ihre Nationalität aus.',
    passportNumberRequired:
      i18n?.manifestValidationPassportNumberRequired ??
      'Bitte geben Sie Ihre Ausweis- / Passnummer an.',
    placeOfBirthRequired:
      i18n?.manifestValidationPlaceOfBirthRequired ??
      'Bitte geben Sie Ihren Geburtsort an.',
    placeOfIssueRequired:
      i18n?.manifestValidationPlaceOfIssueRequired ??
      'Bitte geben Sie den Ausstellungsort Ihres Personalausweises oder Reisepasses an.',
    dateOfBirthRequired:
      i18n?.manifestValidationDateOfBirthRequired ??
      'Bitte geben Sie Ihr Geburtsdatum an.',
    dateOfIssueRequired:
      i18n?.manifestValidationDateOfIssueRequired ??
      'Bitte geben Sie das Ausstellungsdatum Ihres Ausweisdokuments an.',
    validUntilRequired:
      i18n?.manifestValidationValidUntilRequired ??
      'Bitte geben Sie an wie lange Ihr Ausweisdokument gültig ist.',
    professionRequired:
      i18n?.manifestValidationProfessionRequired ??
      'Bitte geben Sie Ihren Beruf an.',
    preferredLanguageRequired:
      i18n?.manifestValidationPreferredLanguageRequired ??
      'Bitte wählen Sie eine bevorzugte Sprache aus.',
    emergencyNameRequired:
      i18n?.manifestValidationEmergencyNameRequired ??
      'Bitte geben Sie Ihren Kontakt für Notfälle an.',
    emergencyPhoneRequired:
      i18n?.manifestValidationEmergencyPhoneRequired ??
      'Bitte geben Sie die Telefonnummer Ihres Kontaktes für Notfälle an.',
    phoneRequired:
      i18n?.manifestValidationPhoneRequired ??
      'Bitte geben Sie Ihre Telefonnummer an.',
    mobileRequired:
      i18n?.manifestValidationMobileRequired ??
      'Bitte geben Sie Ihre Mobilfunknummer an.',

    dateOfBirthPast:
      i18n?.manifestValidationDateOfBirthPast ??
      'Das Geburtsdatum muss in der Vergangenheit liegen.',
    dateOfBirthBeforeEmbark:
      i18n?.manifestValidationDateOfBirthBeforeEmbark ??
      'Das Geburtsdatum muss vor Reiseantritt liegen.',
    dateOfBirthAdultMinAge:
      i18n?.manifestValidationDateOfBirthAdultMinAge ??
      'Erwachsene Reisende müssen bei Reiseantritt mindestens 15 Jahre alt sein.',
    dateOfBirthChildAge:
      i18n?.manifestValidationDateOfBirthChildAge ??
      'Kinder müssen bei Reiseantritt mindestens 2 und unter 15 Jahre alt sein.',
    dateOfBirthBabyAge:
      i18n?.manifestValidationDateOfBirthBabyAge ??
      'Babys müssen bei Reiseantritt unter 2 Jahre alt sein.',
    dateOfBirthMaxAge:
      i18n?.manifestValidationDateOfBirthMaxAge ??
      'Das Geburtsdatum darf nicht weiter als 100 Jahre zurückliegen.',
    validUntilFuture:
      i18n?.manifestValidationValidUntilFuture ??
      'Das Gültigkeitsdatum muss in der Zukunft liegen.',
    dateOfIssueBeforeEmbark:
      i18n?.manifestValidationDateOfIssueBeforeEmbark ??
      'Das Ausstellungsdatum Ihres Ausweisdokuments muss vor dem Reisebeginn liegen.',
    dateOfIssueMaxAge:
      i18n?.manifestValidationDateOfIssueMaxAge ??
      'Das Ausstellungsdatum darf nicht weiter als 15 Jahre zurückliegen.',

    zipCodeFormatDE:
      i18n?.manifestValidationZipCodeFormatDE ??
      'Bitte geben Sie Ihre 5-stellige Postleitzahl an.',
    zipCodeFormatATCH:
      i18n?.manifestValidationZipCodeFormatATCH ??
      'Bitte geben Sie Ihre 4-stellige Postleitzahl an.',
    phoneFormat:
      i18n?.manifestValidationPhoneFormat ??
      'Bitte geben Sie eine gültige Telefonnummer an.',
    mobileFormat:
      i18n?.manifestValidationMobileFormat ??
      'Bitte geben Sie eine gültige Mobilnummer an.',
    passportNumberFormat:
      i18n?.manifestValidationPassportNumberFormat ??
      'Bitte geben Sie eine gültige Ausweis- / Passnummer ohne Leer- und Sonderzeichen an.',
    cityFormat:
      i18n?.manifestValidationCityFormat ??
      'Bitte geben Sie einen gültigen Ort an.',

    approveMobilePhoneDisclosureMustAccept:
      i18n?.manifestValidationApproveMobilePhoneDisclosureMustAccept ??
      'Bitte bestätigen Sie dieses Feld.',
    entryRequirementsMustAccept:
      i18n?.manifestValidationEntryRequirementsMustAccept ??
      'Bitte stimmen Sie den Einreisebestimmungen zu.',
    healthTermsMustAccept:
      i18n?.manifestValidationHealthTermsMustAccept ??
      'Bitte bestätigen Sie dieses Feld.',
    dataProtectionMustAccept:
      i18n?.manifestValidationDataProtectionMustAccept ??
      'Bitte bestätigen Sie dieses Feld.',
    hasETAMustAccept:
      i18n?.manifestValidationHasETAMustAccept ??
      'Bitte bestätigen Sie, dass Sie die Voraussetzungen für die Einreise nach Großbritannien erfüllen.',
    visaMustAccept:
      i18n?.manifestValidationVisaMustAccept ??
      'Bitte bestätigen Sie, dass Sie über ein gültiges Visum für die USA bzw. eine gültige ESTA Anmeldung verfügen.',
    pregnant24WeeksMustAccept:
      i18n?.manifestValidationPregnant24WeeksMustAccept ??
      'Bitte bestätigen Sie dieses Feld.',

    emailInvalid:
      i18n?.manifestValidationEmailInvalid ??
      'Bitte geben Sie eine gültige E-Mail-Adresse an.',
    validUntilAfterIssue:
      i18n?.manifestValidationValidUntilAfterIssue ??
      'Gültig-bis-Datum muss nach dem Ausstellungsdatum liegen.',
    validUntilPassportGrace:
      i18n?.manifestValidationValidUntilPassportGrace ??
      'Ihr Reisepass muss noch mindestens 6 Monate nach Reiseende gültig sein.',
    validUntilIdCardGrace:
      i18n?.manifestValidationValidUntilIdCardGrace ??
      'Ihr Personalausweis muss noch mindestens 3 Monate nach Reiseende gültig sein.',
    dateInvalid:
      i18n?.manifestValidationDateInvalid ??
      'Bitte geben Sie ein gültiges Datum an.',
  };

  const today = startOfToday();
  const embarkDate = opts?.embarkDate ? parseIsoDate(opts.embarkDate) : null;
  const debarkDate = opts?.debarkDate ? parseIsoDate(opts.debarkDate) : null;
  const isFemaleSalutation = opts?.salutation === ManifestSalutation.FRAU;
  const showEtaCheckbox = opts?.showEtaCheckbox === true;
  const showUsaCheckbox = opts?.showUsaCheckbox === true;

  return z
    .object({
      personalInfo: z.object({
        salutation: opts?.salutationRequired
          ? z.string().trim().min(1, msg.salutationRequired)
          : z.string(),
        title: z.string(),
        firstName: z.string().trim().min(1, msg.firstNameRequired),
        lastName: z.string().trim().min(1, msg.lastNameRequired),
      }),

      contact: z
        .object({
          streetAddress: z.string().trim().min(2, msg.streetAddressRequired),
          zipCode: z.string().trim().min(1, msg.zipCodeRequired),
          // Mirrors the Account API regex (`.*(.)(?!\1)..*`): rejects empty,
          // single characters, and runs of one repeated character.
          city: z
            .string()
            .trim()
            .regex(/.*(.)(?!\1)..*/, msg.cityFormat),
          country: z.string().trim().min(1, msg.countryRequired),
          email: z.string().email(msg.emailInvalid),
          phone: z.string(),
          mobile: z.string(),
          approveMobilePhoneDisclosure: z.boolean().refine(v => v, {
            message: msg.approveMobilePhoneDisclosureMustAccept,
          }),
        })
        .superRefine((data, ctx) => {
          // PLZ format is specific for countries (5 digits DE / 4 digits AT/CH).
          // other countries don't trigger validation
          const zipPattern = ZIP_CODE_PATTERNS[data.country];
          if (
            zipPattern &&
            data.zipCode &&
            !zipPattern.test(data.zipCode.trim())
          ) {
            ctx.addIssue({
              code: 'custom',
              message:
                data.country === 'DE'
                  ? msg.zipCodeFormatDE
                  : msg.zipCodeFormatATCH,
              path: ['zipCode'],
            });
          }

          const phoneTrimmed = data.phone.trim();
          if (!phoneTrimmed) {
            ctx.addIssue({
              code: 'custom',
              message: msg.phoneRequired,
              path: ['phone'],
            });
          } else if (!/^\+?\d{8,}$/.test(phoneTrimmed.replace(/\s+/g, ''))) {
            ctx.addIssue({
              code: 'custom',
              message: msg.phoneFormat,
              path: ['phone'],
            });
          }

          const mobileTrimmed = data.mobile.trim();
          if (!mobileTrimmed) {
            ctx.addIssue({
              code: 'custom',
              message: msg.mobileRequired,
              path: ['mobile'],
            });
          } else if (!/^\+?\d{8,}$/.test(mobileTrimmed.replace(/\s+/g, ''))) {
            ctx.addIssue({
              code: 'custom',
              message: msg.mobileFormat,
              path: ['mobile'],
            });
          }
        }),

      credentials: z
        .object({
          passportType: z.string().trim().min(1, msg.passportTypeRequired),
          nationality: z.string().trim().min(1, msg.nationalityRequired),
          passportNumber: z.string().trim().min(1, msg.passportNumberRequired),
          placeOfBirth: z.string().trim().min(1, msg.placeOfBirthRequired),
          placeOfIssue: z.string().trim().min(1, msg.placeOfIssueRequired),
          dateOfBirth: z.string().trim().min(1, msg.dateOfBirthRequired),
          dateOfIssue: z.string().trim().min(1, msg.dateOfIssueRequired),
          birthName: z.string().trim(),
          validUntil: z.string().trim().min(1, msg.validUntilRequired),
          profession: z.string().trim().min(1, msg.professionRequired),
          entryRequirementsAccepted: z
            .boolean()
            .refine(v => v, { message: msg.entryRequirementsMustAccept }),
          // Only required when the USA/ESTA checkbox is actually rendered for
          // this passenger. Otherwise the field is optional (it ships back as
          // `null` via reshapeForApi in ManifestFormBlock).
          hasVisa: showUsaCheckbox
            ? z.boolean().refine(v => v, { message: msg.visaMustAccept })
            : z.boolean(),
          // Only required when the UK-ETA checkbox is rendered.
          hasETA: showEtaCheckbox
            ? z.boolean().refine(v => v, { message: msg.hasETAMustAccept })
            : z.boolean(),
        })
        .superRefine((data, ctx) => {
          const dob = parseIsoDate(data.dateOfBirth);
          const doi = parseIsoDate(data.dateOfIssue);
          const vu = parseIsoDate(data.validUntil);

          // Invalid-date guard: emit before any cross-field check so the user
          // sees "Bitte geben Sie ein gültiges Datum an." instead of e.g.
          // "muss vor Reiseantritt liegen" for an impossible 31-Feb input.
          if (data.dateOfBirth && !dob) {
            ctx.addIssue({
              code: 'custom',
              message: msg.dateInvalid,
              path: ['dateOfBirth'],
            });
          }
          if (data.dateOfIssue && !doi) {
            ctx.addIssue({
              code: 'custom',
              message: msg.dateInvalid,
              path: ['dateOfIssue'],
            });
          }
          if (data.validUntil && !vu) {
            ctx.addIssue({
              code: 'custom',
              message: msg.dateInvalid,
              path: ['validUntil'],
            });
          }

          // Date of birth must be on or before embark. The bracket-specific
          // age check lives in the root-level superRefine — it depends on the
          // live `personalInfo.salutation` form value, which this
          // section-scoped refine cannot see. Falls back to "must be in the
          // past" when embark is unknown.
          if (dob && embarkDate) {
            if (dob.toMillis() > embarkDate.toMillis()) {
              ctx.addIssue({
                code: 'custom',
                message: msg.dateOfBirthBeforeEmbark,
                path: ['dateOfBirth'],
              });
            }
          } else if (dob && dob.toMillis() > today.toMillis()) {
            ctx.addIssue({
              code: 'custom',
              message: msg.dateOfBirthPast,
              path: ['dateOfBirth'],
            });
          }
          // Hard upper bound: dob may not be more than 100 years ago. UI
          // matches via `minYear={now - 100}` on the Birthdate field. Only
          // fires when dob is otherwise plausible (not in the future), to
          // avoid stacking with the past-check above.
          if (
            dob &&
            dob.toMillis() <= today.toMillis() &&
            ageInYearsAt(dob, today) > 100
          ) {
            ctx.addIssue({
              code: 'custom',
              message: msg.dateOfBirthMaxAge,
              path: ['dateOfBirth'],
            });
          }
          // All `validUntil` rules in priority order — only the first matching
          // branch fires, so the field never shows two stacked messages.
          // Reading top to bottom: past → before-issue → type-specific grace
          // period after debark ("R" = Reisepass +6mo, "P" = Personalausweis
          // +3mo, anything else has no rule).
          const graceMonths =
            data.passportType === 'R' ? 6 : data.passportType === 'P' ? 3 : 0;
          if (vu) {
            if (vu.toMillis() <= today.toMillis()) {
              ctx.addIssue({
                code: 'custom',
                message: msg.validUntilFuture,
                path: ['validUntil'],
              });
            } else if (doi && vu.toMillis() <= doi.toMillis()) {
              ctx.addIssue({
                code: 'custom',
                message: msg.validUntilAfterIssue,
                path: ['validUntil'],
              });
            } else if (
              debarkDate &&
              graceMonths > 0 &&
              vu.toMillis() <
                debarkDate.plus({ months: graceMonths }).toMillis()
            ) {
              ctx.addIssue({
                code: 'custom',
                message:
                  data.passportType === 'R'
                    ? msg.validUntilPassportGrace
                    : msg.validUntilIdCardGrace,
                path: ['validUntil'],
              });
            }
          }

          // Passport/id number: alphanumeric (letters + digits only), min 5.
          // Only runs when the user entered something — emptiness is covered
          // by `.min(1, required)`.
          if (
            data.passportNumber &&
            !/^[A-Za-z0-9]{5,}$/.test(data.passportNumber)
          ) {
            ctx.addIssue({
              code: 'custom',
              message: msg.passportNumberFormat,
              path: ['passportNumber'],
            });
          }

          // Date of issue must be strictly before embark. No "past" check —
          // an issue date today or slightly in the future is acceptable, as
          // long as it precedes the trip. Skips when embark is unknown.
          if (doi && embarkDate && doi.toMillis() >= embarkDate.toMillis()) {
            ctx.addIssue({
              code: 'custom',
              message: msg.dateOfIssueBeforeEmbark,
              path: ['dateOfIssue'],
            });
          }

          // Date of issue may not be more than 15 years in the past — mirrors
          // the Account API's hard backend check ("DateOfIssue must be between
          // today-15y and today"). Skip when the date is invalid (already
          // covered by the invalid-date guard above).
          if (doi) {
            const earliestIssue = today.minus({ years: 15 });
            if (doi.toMillis() < earliestIssue.toMillis()) {
              ctx.addIssue({
                code: 'custom',
                message: msg.dateOfIssueMaxAge,
                path: ['dateOfIssue'],
              });
            }
          }
        }),

      otherInformation: z
        .object({
          mealRequest: z.string(),
          individualRequest: z.string().trim(),
          allergy: z.array(z.string()),
          allergyInfo: z.string().trim(),
          handicap: z.string(),
          emergencyContactName: z
            .string()
            .trim()
            .min(1, msg.emergencyNameRequired),
          emergencyContactPhone: z.string(),
          acceptDataProtectionTerms: z.boolean(),
          acceptHealthTerms: z
            .boolean()
            .refine(v => v, { message: msg.healthTermsMustAccept }),
          // Render field and validate only for salutation 'Frau'
          pregnant24Weeks: isFemaleSalutation
            ? z
                .boolean()
                .refine(v => v, { message: msg.pregnant24WeeksMustAccept })
            : z.boolean(),
          preferredLanguageCode: z
            .string()
            .trim()
            .min(1, msg.preferredLanguageRequired),
        })
        .superRefine((data, ctx) => {
          const emergencyPhone = data.emergencyContactPhone;
          if (!emergencyPhone) {
            ctx.addIssue({
              code: 'custom',
              message: msg.emergencyPhoneRequired,
              path: ['emergencyContactPhone'],
            });
          } else {
            const digits = emergencyPhone.replace(/\s+/g, '');
            if (!/^\+?\d{8,}$/.test(digits)) {
              ctx.addIssue({
                code: 'custom',
                message: msg.phoneFormat,
                path: ['emergencyContactPhone'],
              });
            }
          }

          const hasHealthData =
            !!data.mealRequest ||
            !!data.individualRequest ||
            data.allergy.length > 0 ||
            !!data.allergyInfo ||
            !!data.handicap;
          if (hasHealthData && !data.acceptDataProtectionTerms) {
            ctx.addIssue({
              code: 'custom',
              message: msg.dataProtectionMustAccept,
              path: ['acceptDataProtectionTerms'],
            });
          }
        }),
    })
    .superRefine((data, ctx) => {
      // Bracket-specific age check against the *live* salutation form value —
      // for selectable child/baby salutations the bracket follows the user's
      // current choice, not the prefetched manifest value:
      //   adult (Herr/Frau): age ≥ 15
      //   kind  (Kind*):     2 ≤ age < 15
      //   baby  (Baby*):     0 ≤ age < 2
      // Unknown salutations skip the check. The dob-vs-embark bounds are
      // validated in the credentials section refine; this only brackets.
      const ageBracket = getManifestAgeBracket(data.personalInfo.salutation);
      const dob = parseIsoDate(data.credentials.dateOfBirth);
      if (!dob || !embarkDate || dob.toMillis() > embarkDate.toMillis()) {
        return;
      }

      const ageAtEmbark = ageInYearsAt(dob, embarkDate);
      if (ageBracket === 'adult' && ageAtEmbark < 15) {
        ctx.addIssue({
          code: 'custom',
          message: msg.dateOfBirthAdultMinAge,
          path: ['credentials', 'dateOfBirth'],
        });
      } else if (
        ageBracket === 'kind' &&
        (ageAtEmbark < 2 || ageAtEmbark >= 15)
      ) {
        ctx.addIssue({
          code: 'custom',
          message: msg.dateOfBirthChildAge,
          path: ['credentials', 'dateOfBirth'],
        });
      } else if (ageBracket === 'baby' && ageAtEmbark >= 2) {
        ctx.addIssue({
          code: 'custom',
          message: msg.dateOfBirthBabyAge,
          path: ['credentials', 'dateOfBirth'],
        });
      }
    });
};
